/**
 * The one per-city record for the LOCI platform (API-1704).
 *
 * Every per-city fact that more than one surface needs — the public 3-letter
 * code, the display name, the `Location.city` string Prisma filters on, and
 * the IANA timezone its wall clock reads — lives here and nowhere else.
 *
 * ## Why this file exists
 *
 * Before API-1704 the same facts were spread across four structures keyed by
 * the same code with nothing linking them: `CityEdition` (code + displayName),
 * `apps/web/lib/home/cityTimezone.ts` (`CITY_TIMEZONES`, four cities, whose
 * own docblock called itself the single source of truth), and two maps in
 * `@loci/validation` (`VENUE_CITY_CODE_MAP` → `Location.city`,
 * `VENUE_CITY_TIMEZONE_MAP` → zone, both `{ CLT }`). Adding a market was a
 * four-file edit, and missing one file failed silently and surface-specifically:
 * a venue route that 400s, a `late-night` facet computed against the wrong
 * clock, a home feed bucketing into the wrong day. The only guard — a key-set
 * parity test — was scoped inside a single file, so a city added with a
 * Charlotte-copied zone in the web map and a correct one in validation failed
 * nothing.
 *
 * Every one of those registries is now a **derived view** of this record, so
 * the drift is unrepresentable rather than merely tested for.
 *
 * ## WEB-389's declined decision, revisited (API-1704)
 *
 * WEB-389 spec'd `CITY_EDITIONS[cityCode].timezone` and the implementation
 * declined it, keeping a local web map instead, on the reasoning that the web
 * map "has been the de-facto contract since the greeting bar landed". That was
 * defensible with two registries. It stopped being defensible at three: API-1692
 * added `VENUE_CITY_TIMEZONE_MAP` as an independent copy of the same CLT zone,
 * which is the evidence the decision needed revisiting. The zone is now on the
 * shared record — but on `CityRecord`, not on `CityEdition`, which is the part
 * WEB-389 could not have known it wanted: a *registered edition* implies routing
 * and map defaults, and CHI/MIA/NYC need zones long before they need either.
 *
 * ## Adding a market
 *
 * One edit: a new entry below. `CityCode` is `keyof typeof CITY_RECORDS`, so the
 * union widens with it and every `satisfies Record<CityCode, …>` derived view
 * picks the city up at compile time. Registering a full {@link CityEdition}
 * (map center, radius, theme, copy) is a **separate, later** step — that is what
 * turns a recognised code into a routed, queryable market.
 *
 * @packageDocumentation
 */

/**
 * Geographic point, in the registry's `lat` / `lon` spelling. Wire formats
 * that spell longitude `lng` (`/events/discover`) rename it at their own
 * boundary (`getCityAnchor` in web, `resolveCityAnchor` in backend-api).
 */
export interface CityCenter {
  lat: number;
  lon: number;
}

/**
 * Axis-aligned bounding box in degrees. `north`/`south` are latitudes,
 * `east`/`west` longitudes.
 */
export interface CityBounds {
  readonly north: number;
  readonly south: number;
  readonly east: number;
  readonly west: number;
}

/**
 * Where a city's Discover query is centred by default, and how far it reaches.
 * Kept separate from {@link CityGeography.center} on purpose: the centroid is a
 * fact about the city, the anchor is a product default that may later move
 * (e.g. to a nightlife district) without re-pointing every map.
 */
export interface CityDefaultAnchor {
  readonly center: CityCenter;
  readonly radiusKm: number;
}

/**
 * A city's geography (COR-155, ruling R22): every coordinate a surface needs
 * to place the market, so none of them is a literal in a reader.
 */
export interface CityGeography {
  /** City centroid. */
  readonly center: CityCenter;
  /**
   * Bounding box of the default Discover query circle — derived from
   * {@link defaultAnchor} by {@link boundsAroundAnchor}, never hand-typed, so the
   * box and the circle cannot disagree.
   */
  readonly bounds: CityBounds;
  /** Default Discover anchor: centre plus search radius. */
  readonly defaultAnchor: CityDefaultAnchor;
}

/** Mean length of one degree of latitude, in km. */
const KM_PER_DEGREE_LATITUDE = 111.32;

/**
 * The bounding box that just contains a circle of `radiusKm` around `center`
 * (equirectangular approximation, accurate to well under 1% at city scale).
 */
export const boundsAroundAnchor = ({ center, radiusKm }: CityDefaultAnchor): CityBounds => {
  const latDelta = radiusKm / KM_PER_DEGREE_LATITUDE;
  const lonDelta = radiusKm / (KM_PER_DEGREE_LATITUDE * Math.cos((center.lat * Math.PI) / 180));
  return {
    north: center.lat + latDelta,
    south: center.lat - latDelta,
    east: center.lon + lonDelta,
    west: center.lon - lonDelta,
  };
};

/** Charlotte's centroid — the one place these coordinates are written. */
const CLT_CENTER: CityCenter = { lat: 35.2271, lon: -80.8431 };
const CLT_DEFAULT_ANCHOR: CityDefaultAnchor = { center: CLT_CENTER, radiusKm: 25 };

/**
 * Per-city facts that every surface may need, for every code LOCI recognises —
 * launched or upcoming.
 *
 * Deliberately smaller than {@link CityEdition}: a code earns a record the
 * moment it appears in a picker, and earns an edition only when the market is
 * routable. Keeping the two apart is what lets `CHI` carry Central time without
 * also claiming a map center and a theme it has not been designed yet.
 */
export interface CityRecord {
  /**
   * Public 3-letter code. Typed `string` here rather than `CityCode` because
   * `CityCode` is derived from this registry's keys; the
   * {@link CITY_RECORD_CODES_MATCH_KEYS} assertion below is the compile-time
   * proof that each entry's `code` equals its own key.
   */
  readonly code: string;
  /** Human-facing city name, e.g. for pickers and page copy. */
  readonly displayName: string;
  /**
   * The `Location.city` value rows for this market carry. Separate from
   * {@link displayName} on purpose: display copy is free to change (or be
   * localised) without silently re-pointing every Prisma filter in the repo.
   */
  readonly locationCity: string;
  /**
   * USPS two-letter code of the state the city is in (`'NC'`). Feeds write it
   * after the city name when they have no venue (`"Charlotte, NC"`), which is
   * how COR-124's placeholder verdict (`isPlaceholderVenueName` in
   * `@loci/validation`) recognises a bare city as a non-venue label without
   * hardcoding any market's name.
   */
  readonly stateCode: string;
  /**
   * IANA timezone whose wall clock this city's surfaces read. Every surface
   * that turns a stored UTC instant into a *local hour* — late-night facets,
   * density heatmaps, "tonight" windows, day-headed rails — resolves here
   * rather than inlining `'America/New_York'`.
   */
  readonly timeZone: string;
  /**
   * Centre, bounds and default anchor (COR-155). Optional because a recognised
   * code earns geography when its market is designed, not when it first
   * appears in a picker; {@link CityEdition} makes it required, so every
   * *served* city has it.
   */
  readonly geography?: CityGeography;
}

/**
 * The registry. Keys are the public codes the `?city=` / `[city]` route
 * segment carries.
 *
 * `satisfies Record<string, CityRecord>` keeps every entry complete while
 * leaving the keys literal, so {@link CityCode} can be derived from them.
 */
export const CITY_RECORDS = {
  CLT: {
    code: 'CLT',
    displayName: 'Charlotte',
    locationCity: 'Charlotte',
    stateCode: 'NC',
    timeZone: 'America/New_York',
    geography: {
      center: CLT_CENTER,
      bounds: boundsAroundAnchor(CLT_DEFAULT_ANCHOR),
      defaultAnchor: CLT_DEFAULT_ANCHOR,
    },
  },
  CHI: {
    code: 'CHI',
    displayName: 'Chicago',
    locationCity: 'Chicago',
    stateCode: 'IL',
    timeZone: 'America/Chicago',
  },
  MIA: {
    code: 'MIA',
    displayName: 'Miami',
    locationCity: 'Miami',
    stateCode: 'FL',
    timeZone: 'America/New_York',
  },
  NYC: {
    code: 'NYC',
    displayName: 'New York',
    locationCity: 'New York',
    stateCode: 'NY',
    timeZone: 'America/New_York',
  },
} as const satisfies Record<string, CityRecord>;

/**
 * Discriminated union of every city code LOCI recognises.
 *
 * Derived from {@link CITY_RECORDS} rather than hand-written, so "add a market"
 * cannot mean "add the code to the union and forget the facts". `CLT` is the
 * only code with a registered {@link CityEdition} today; the others are
 * upcoming editions surfaced in user-facing pickers, and runtime narrowing via
 * `CITY_EDITIONS` keeps them out of routing paths.
 */
export type CityCode = keyof typeof CITY_RECORDS;

/**
 * `true` when every record's `code` field equals the key it is filed under,
 * `never` when any does not — the one invariant `satisfies Record<string,
 * CityRecord>` cannot express, and the one that would let `CITY_RECORDS.MIA.code`
 * read `'CLT'` and silently mislabel every `Location.city` → code reverse lookup.
 */
export type CityRecordCodesMatchKeys = typeof CITY_RECORDS extends { [K in CityCode]: { code: K } }
  ? true
  : never;

/**
 * The assertion that makes {@link CityRecordCodesMatchKeys} load-bearing.
 *
 * A bare `type X = never` is perfectly legal TypeScript, so the alias alone
 * proves nothing — it has to be *assigned to*. When the registry is consistent
 * this is `const _: true = true`; when it is not, it is `const _: never = true`,
 * which fails `pnpm type-check` with `TS2322`. Exported (rather than a local
 * `_`) so no unused-symbol rule can delete the guard.
 */
export const CITY_RECORD_CODES_MATCH_KEYS: CityRecordCodesMatchKeys = true;

/** Narrows an arbitrary string to a registered {@link CityCode}. */
export const isCityCode = (code: string): code is CityCode =>
  Object.prototype.hasOwnProperty.call(CITY_RECORDS, code);

/**
 * The full record for a code, or `undefined` when the code is not registered.
 *
 * Exact-match on purpose: callers that accept user input normalise it first
 * (`venueCityCodeSchema` trims and upper-cases), and a lookup that quietly
 * accepted `' clt '` here would diverge from the schemas that don't.
 */
export const getCityRecord = (code: string): CityRecord | undefined =>
  isCityCode(code) ? CITY_RECORDS[code] : undefined;
